import { formatDistanceToNow, parseISO } from 'date-fns';
import React from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import { HeadType, RowType } from '@atlaskit/dynamic-table/dist/types/types';
import { ApprovalStatus as ApprovalStatusType, Task } from '@css-xp/apd-common';
import { FormattedDate, Icon } from '@css-xp/base';
import { ApprovalStatus, withTooltip } from '../../common';
import { statusToShow } from '../../common/status/status';
import { DisplayRequestType } from '../../constants/approvals';
import { TABLE_COLUMN_KEYS, TABLE_HEADER } from '../../constants/table';
import { getEntityTypeUrl } from '../../utils';
import { TaskDetailAction } from '../actions';

export interface CreateTableRowsParam {
  taskslist: Task[];
  refetch: () => void;
  selectRows: (_param: Task) => void;
  selectedRowsTaskIdList: Set<string>;
  hasTaskManagerWriteAccess?: boolean;
}

const getApprovalStatusContent = (status: ApprovalStatusType | undefined | null) => {
  if (!status) {
    return <></>;
  }
  return withTooltip(
    statusToShow[status] ? statusToShow[status].toUpperCase() : status,
    <ApprovalStatus status={status} />
  );
};

export const createTableHead = (
  withWidth: boolean,
  handleHeaderCheckbox: () => void,
  selectedRowsTaskIdList: Set<string>,
  hasTaskManagerWriteAccess?: boolean
): HeadType => {
  return {
    cells: [
      ...(hasTaskManagerWriteAccess
        ? [
            {
              key: TABLE_COLUMN_KEYS.CHECKBOX,
              content: (
                <Checkbox
                  testId="header-checkbox"
                  onChange={handleHeaderCheckbox}
                  isChecked={!!selectedRowsTaskIdList.size}
                  isDisabled={!selectedRowsTaskIdList.size}
                />
              ),
              shouldTruncate: true,
              width: withWidth ? 5 : undefined,
              testId: TABLE_COLUMN_KEYS.CHECKBOX,
            },
          ]
        : []),
      {
        key: TABLE_COLUMN_KEYS.URGENT,
        content: TABLE_HEADER.URGENT,
        shouldTruncate: true,
        width: withWidth ? 10 : undefined,
        testId: TABLE_COLUMN_KEYS.URGENT,
      },
      {
        key: TABLE_COLUMN_KEYS.TASK_NUMBER,
        content: TABLE_HEADER.TASK_DISPLAY_ID,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.TASK_NUMBER,
      },
      {
        key: TABLE_COLUMN_KEYS.CREATED_DATE,
        content: TABLE_HEADER.REQUEST_DATE,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.CREATED_DATE,
      },
      {
        key: TABLE_COLUMN_KEYS.REQUEST_TYPE,
        content: TABLE_HEADER.REQUEST_TYPE,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.REQUEST_TYPE,
      },
      {
        key: TABLE_COLUMN_KEYS.BUSINESS_KEY,
        content: TABLE_HEADER.ENTITY_Id,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.BUSINESS_KEY,
      },
      {
        key: TABLE_COLUMN_KEYS.STATUS,
        content: TABLE_HEADER.STATUS,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.STATUS,
      },
      {
        key: TABLE_COLUMN_KEYS.INITIATOR,
        content: TABLE_HEADER.INITIATOR,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.INITIATOR,
      },
      {
        key: TABLE_COLUMN_KEYS.GROUP_ID,
        content: TABLE_HEADER.GROUP_ID,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.GROUP_ID,
      },
      {
        key: TABLE_COLUMN_KEYS.APPROVER,
        content: TABLE_HEADER.APPROVER,
        shouldTruncate: true,
        width: withWidth ? 20 : undefined,
        testId: TABLE_COLUMN_KEYS.APPROVER,
      },
    ],
  };
};

export const createTableRows = ({
  taskslist,
  refetch,
  selectRows,
  selectedRowsTaskIdList,
  hasTaskManagerWriteAccess,
}: CreateTableRowsParam): Array<RowType> => {
  return taskslist.map((task: Task, index: number) => {
    const date = task.createdDate ? parseISO(task.createdDate) : null;
    const initiator = task?.initiatorName ?? '';
    const displayRequestType = (task?.requestType && DisplayRequestType[task.requestType]) ?? '';
    const taskDisplayId = task?.taskNumber ?? '';
    const approver = task.ownerName || task.ownerId || task.groupId || '';
    const Urgent = task.dueDate ? new Date() > new Date(task.dueDate) : false;
    const groupId = task?.groupId ?? '';
    return {
      key: `row-${index}`,
      cells: [
        ...(hasTaskManagerWriteAccess
          ? [
              {
                key: TABLE_COLUMN_KEYS.CHECKBOX + index,
                content: (
                  <Checkbox
                    testId={`row-checkbox-${task.taskId}`}
                    onChange={() => selectRows(task)}
                    isChecked={selectedRowsTaskIdList.has(task.taskId)}
                  />
                ),
                data: '',
              },
            ]
          : []),
        {
          key: TABLE_COLUMN_KEYS.URGENT + index,
          content: Urgent
            ? withTooltip(<FormattedDate date={task.dueDate} showTime />, <Icon type="warning" size="medium" />)
            : '',
          data: Urgent,
        },
        {
          key: TABLE_COLUMN_KEYS.TASK_NUMBER + index,
          content: withTooltip(taskDisplayId, <TaskDetailAction task={task} refetch={refetch} refetchAll={refetch} />),
          data: taskDisplayId,
        },
        {
          key: date ? date.valueOf() : 0,
          content: date
            ? withTooltip(
                <FormattedDate date={task.createdDate} showTime />,
                <div className="text-overflow">{formatDistanceToNow(date, { addSuffix: true })}</div>
              )
            : '',
          data: date ? formatDistanceToNow(date, { addSuffix: true }) : '',
        },
        {
          key: TABLE_COLUMN_KEYS.REQUEST_TYPE + index,
          content: withTooltip(displayRequestType, <div className="text-overflow">{displayRequestType}</div>),
          data: displayRequestType,
        },
        {
          key: TABLE_COLUMN_KEYS.BUSINESS_KEY + index,
          content: withTooltip(
            task.businessKey,
            <a href={getEntityTypeUrl(task?.entityType, task?.businessKey, task?.entityId)}>
              <div className="text-overflow">{task.businessKey}</div>
            </a>
          ),
          data: task?.businessKey,
        },
        {
          key: TABLE_COLUMN_KEYS.STATUS + index,
          content: getApprovalStatusContent(task.approvalStatus),
          data:
            task.approvalStatus && statusToShow[task.approvalStatus]
              ? statusToShow[task.approvalStatus].toUpperCase()
              : '',
        },
        {
          key: TABLE_COLUMN_KEYS.INITIATOR + index,
          content: withTooltip(initiator, <div className="text-overflow">{initiator}</div>),
          data: initiator,
        },
        {
          key: TABLE_COLUMN_KEYS.GROUP_ID + index,
          content: withTooltip(groupId, <div className="text-overflow">{groupId}</div>),
          data: groupId,
        },
        {
          key: TABLE_COLUMN_KEYS.APPROVER + index,
          content: withTooltip(approver, <div className="text-overflow">{approver}</div>),
          data: approver,
        },
      ],
    };
  });
};
